import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { formatCount } from "../../../utils/numberFormat";

const StatusDistributionChart = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm h-80 animate-pulse">
        <div className="h-6 bg-gray-300 rounded w-1/3 mb-4"></div>
        <div className="h-64 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (!data?.charts) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm text-center">
        <p className="text-gray-500">No status distribution data available</p>
      </div>
    );
  }

  const barData = data.charts.bar.labels.map((label, index) => ({
    name: label,
    count: data.charts.bar.datasets[0].data[index],
    color: data.charts.bar.datasets[0].backgroundColor[index],
    percentage: data.detailed?.[label]?.percentage || "0.00",
  }));

  const pieData = data.charts.pie.labels.map((label, index) => ({
    name: label,
    value: data.charts.pie.datasets[0].data[index],
    color: data.charts.pie.datasets[0].backgroundColor[index],
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-300 rounded-lg shadow-lg">
          <p className="font-bold text-gray-800">{payload[0].payload.name}</p>
          <p className="text-sm">
            Count: <span className="font-bold">{payload[0].value}</span>
          </p>
          <p className="text-sm">
            Percentage:{" "}
            <span className="font-bold">
              {payload[0].payload.percentage || "0.00"}%
            </span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-800 text-balance">
          Implementation Status Distribution
        </h3>
        <div className="text-sm text-gray-600">
          Total:{" "}
          <span className="font-bold">
            {formatCount(data.summary?.totalImplementations)}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-4">
            Bar Chart View
          </h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={barData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  tick={{ fontSize: 12 }}
                />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="count"
                  name="Implementations"
                  radius={[4, 4, 0, 0]}
                >
                  {barData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-4">
            Pie Chart View
          </h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name.split(" ")[0]}: ${(percent * 100).toFixed(1)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 mb-3">
          Status Breakdown
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(data.detailed || {}).map(([status, details]) => (
            <div
              key={status}
              className="bg-gray-50 rounded-lg p-4 border border-gray-200"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <div
                    className="w-3 h-3 rounded-full mr-2"
                    style={{
                      backgroundColor:
                        data.charts.bar.datasets[0].backgroundColor[
                          data.charts.bar.labels.indexOf(status)
                        ] || "#888",
                    }}
                  ></div>
                  <span className="text-sm font-medium text-gray-700">
                    {status}
                  </span>
                </div>
                <span className="text-lg font-bold text-gray-900">
                  {formatCount(details.count)}
                </span>
              </div>
              <div className="text-xs text-gray-600">
                {details.percentage}% of total
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StatusDistributionChart;
